import { useState } from "react";
import { Modal, Form } from "react-bootstrap";
import { useForm } from "react-hook-form";
import { Button } from "./Controls/Button";
import { Input } from "./Controls/Input";
import { HeaderText } from "./Controls/HeaderText";
import { MoneyInput } from "./Controls/MoneyInput";
import { Money, showMoney, toCents } from "./Data/Money";

interface PaymentAdjustModalProps {
    currentAmount: Money;
    show:          boolean;
    setShow:       ( show:       boolean           ) => void;
    onSave:        ( adjustData: PaymentAdjustForm ) => void;
}

export interface PaymentAdjustForm { 
    newAmount: number;
    note:      string;
}

export function PaymentAdjustModal( { currentAmount: remaining, show, setShow, onSave }: PaymentAdjustModalProps ) {
    const [isFullReversal, setIsFullReversal] = useState( false );

    const { register, watch, setValue, formState: { errors }, reset, handleSubmit } = useForm<PaymentAdjustForm>({
        defaultValues: {
            newAmount: 0,
            note:   "", 
         }
    } );
    
    function close() {
        reset();
        setIsFullReversal( false );
        setShow( false );
    }

    function submit( data: PaymentAdjustForm ) {
        onSave( { ...data, newAmount: isFullReversal ? 0 : data.newAmount } );
        close();
    }

    const note = watch( "note" );
    const newAmount = watch( "newAmount" );

    function handleFullReversalChange( checked: boolean ) {
        setIsFullReversal( checked );
        if( checked && newAmount !== 0 ) {
            setValue( "newAmount", 0 );
        }
    }

    const canFullReversal = toCents( remaining ) !== 0;

    return <Modal show={show} centered onEscapeKeyDown={close}>
        <Form onSubmit={handleSubmit(submit)}>
            <Modal.Header className="fs-5">
                <HeaderText>
                    Adjust Payment
                </HeaderText>
            </Modal.Header>
            <Modal.Body>
                <p className="p-2 text-secondary">
                    Use this page to set the payment amount to a new value.
                    You should only use this when there was an error in data entry 
                    such as entering 10 when you intended 100 
                </p>
                <Input
                    type="text"
                    label="Note"
                    maxLength={64}
                    placeholder="Accidentally Entered 10 instead of 100"
                    explanation={`Why is an adjustment needed? ${note.length}/64`}
                    {...register( "note", {
                        required: "Note is required"
                    })}
                    error={errors.note} />

                <Input
                    inputClassName="text-end"
                    type="text"
                    label="Current Payment Amount"
                    disabled
                    value={showMoney( remaining )}
                    placeholder=""
                    explanation={`The current value of the payment in the system`} />

                {canFullReversal &&
                    <Form.Group className="mb-3">
                        <Form.Check
                            type="checkbox"
                            id="payment-adjust-full-reversal"
                            label="This is a full reversal"
                            checked={isFullReversal}
                            onChange={( e ) => handleFullReversalChange( e.target.checked )} />
                        {isFullReversal &&
                            <Form.Text className="d-block text-muted ms-4">
                                The full amount of the payment ({showMoney( remaining )}) will be reversed
                            </Form.Text>}
                    </Form.Group>}

                {!isFullReversal &&
                    <MoneyInput
                        label="New Amount"
                        inputClassName="text-end"
                        explanation="The payment's amount will be set to this amount"
                        min="0"
                        value={newAmount}
                        {...register("newAmount", {
                            valueAsNumber: true,
                            required:      "Amount is required",
                            validate: (value: number) => value >= 0 && (!isNaN(value)) || "Please enter a valid number"
                        })}
                        error={errors.newAmount} />}
                    
            </Modal.Body>
            <Modal.Footer className="d-flex">
                <Button className="flex-grow-1" onClick={close}>Cancel</Button>
                <Button className="flex-grow-1 btn-main" type="submit">Save</Button>
            </Modal.Footer>
        </Form>
    </Modal>;
}